import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import {
  AdvertiserManager,
  DefaultAdvertiserManager
} from 'core/advertiser/AdvertiserManager';
import i18n from 'i18next';
import _ from 'lodash';
import { FormikProps } from 'formik';
import { toast } from 'react-toastify';
import { validateEmpty, validateUrl } from 'utils/ValidateUtils';
import {
  AdRequestSourceManager,
  DefaultAdRequestSourceManager
} from 'core/adRequestSource/AdRequestSourceManager';
import { DynamicBreadcrumb } from 'components/Breadcrumbs/DynamicBreadcrumbs';
import { AgencyManager, DefaultAgencyManager } from 'core';

export type AdvertiserFormState = {
  readonly loading: boolean;
  readonly redirectPath?: string;
  readonly categories?: SelectOptions[];
  readonly publisherOptions: SelectOptions[];
};
export interface AdvertiserFormModel {
  readonly advertiserId?: string | number;
  readonly state: AdvertiserFormState;
  readonly event: UpdateEventListener<AdvertiserFormModel>;
  readonly title: string;
  readonly advertiser?: AdvertiserFormData;
  readonly formikProps?: FormikProps<AdvertiserFormData>;
  readonly breadcrumbs: any[];
  readonly agencyManager: AgencyManager;
  init(): Promise<void>;
  validate(advertiser: AdvertiserFormData): any;
  validateAndSetError(): any;
  setFormikProps(props: FormikProps<AdvertiserFormData>): void;
  submit(): void;
  onUnmount(eventHandler): void;
}

export type AdvertiserFormProps = {
  readonly model: AdvertiserFormModel;
};

export type AdvertiserFormData = {
  id?: number;
  agencyId: number;
  advertiserName: string;
  website: string;
  category?: string;
  note?: string;
};

abstract class DefaultAdvertiserFormModel implements AdvertiserFormModel {
  event: FireableUpdateEventListener<AdvertiserFormModel>;
  loading: boolean;
  advertiser: any;
  formikProps?: FormikProps<AdvertiserFormData>;
  redirectPath?: string;
  categories?: SelectOptions[];
  publisherOptions: SelectOptions[] = [];

  constructor(
    private adRequestSourceManager: AdRequestSourceManager = new DefaultAdRequestSourceManager(),
    protected manager: AdvertiserManager = new DefaultAdvertiserManager(),
    public agencyManager: AgencyManager = new DefaultAgencyManager()
  ) {
    this.event = new FireableUpdateEventListener<AdvertiserFormModel>();
    this.loading = true;
    this.adRequestSourceManager = adRequestSourceManager;
    this.manager = manager;
  }

  abstract get title();

  abstract initAdvertiser(): Promise<void>;

  abstract submit(): void;

  abstract get breadcrumbs();

  async initPublisherOptions(agencyId: string | number | null) {
    if (!agencyId) {
      return;
    }
    this.updateState(true);
    const publishers = await this.agencyManager.getAgencyPublishers(+agencyId);
    this.publisherOptions = publishers.map((publisher) => ({
      label: publisher,
      value: publisher
    }));
    this.updateState(false);
  }

  async init() {
    this.updateState(true);
    try {
      await this.initAdvertiser();
      const categories =
        await this.adRequestSourceManager.getTenmaxCategories();
      this.categories = categories.map((category) => {
        return {
          label: i18n.t(`tenmaxCategory.labels.${category.label}`),
          value: category.value
        };
      });
      if (!_.isEmpty(this.advertiser.category)) {
        const advertiserCategory = this.categories.find(
          (category) => category.value === this.advertiser.category
        );
        !advertiserCategory &&
          this.categories.push({
            label: i18n.t(
              `tenmaxCategory.labels.${this.advertiser.category.toLowerCase().replace('-', '_')}`
            ),
            value: this.advertiser.category
          });
      }
    } catch (e) {}
    this.updateState(false);
  }

  validate = (advertiser: AdvertiserFormData) => {
    return _.omitBy(
      {
        advertiserName: validateEmpty(advertiser.advertiserName),
        website: validateUrl(advertiser.website),
        category: validateEmpty(advertiser.category)
      },
      _.isUndefined
    );
  };

  setFormikProps(props: FormikProps<AdvertiserFormData>) {
    this.formikProps = props;
  }

  onUnmount = (eventHandler) => {
    this.event.remove(eventHandler);
    this.redirectPath = undefined;
  };

  validateAndSetError = () => {
    if (!this.formikProps) {
      return {};
    }

    const errors = this.validate(this.formikProps.values);
    this.formikProps && this.formikProps.setErrors(errors);
    const touched = {};
    this.generateFormikTouchedObj(errors, touched);
    this.formikProps && this.formikProps.setTouched(touched);
    return errors;
  };

  generateFormikTouchedObj(errors, touched) {
    let keys = Object.keys(errors);
    keys.forEach((key) => {
      let value = errors[key];
      if (typeof value === 'object') {
        touched[key] = {};
        this.generateFormikTouchedObj(errors[key], touched[key]);
      } else {
        touched[key] = true;
      }
    });
  }

  get state(): AdvertiserFormState {
    return {
      loading: this.loading,
      redirectPath: this.redirectPath,
      categories: this.categories,
      publisherOptions: this.publisherOptions
    };
  }

  getRedirectPath(advertiserId: number | string) {
    return `/advertisers/${advertiserId}`;
  }

  updateState(loading: boolean, redirectPath?: string) {
    this.loading = loading;
    this.redirectPath = redirectPath;
    this.event.fireEvent(this);
  }
}

export class CreateAdvertiserFormModel extends DefaultAdvertiserFormModel {
  agencyId: string | number | null;

  constructor(
    agencyId: string | number | null,
    adRequestSourceManager: AdRequestSourceManager = new DefaultAdRequestSourceManager(),
    manager: AdvertiserManager = new DefaultAdvertiserManager(),
    agencyManager: AgencyManager = new DefaultAgencyManager()
  ) {
    super(adRequestSourceManager, manager, agencyManager);
    this.agencyId = agencyId;
  }

  get breadcrumbs() {
    return [
      {
        path: '/advertisers',
        breadcrumb: i18n.t('appMenus.comapnyManagement.items.advertisers')
      },
      { path: '/advertisers/new', breadcrumb: this.title }
    ];
  }

  async initAdvertiser(): Promise<void> {
    this.advertiser = {
      advertiserName: '',
      website: 'https://',
      comment: '',
      agencyId: this.agencyId
    };
    await this.initPublisherOptions(this.agencyId);
  }

  async submit() {
    if (!this.formikProps) {
      return;
    }
    const newAdvertiser = this.formikProps.values;
    this.updateState(true);
    try {
      const advertiserId = await this.manager.createAdvertiser(newAdvertiser);
      this.updateState(false, this.getRedirectPath(advertiserId));
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.updateState(false);
    }
  }

  get title() {
    return i18n.t('advertiserForm.labels.createTitle');
  }
}

export class EditAdvertiserFormModel extends DefaultAdvertiserFormModel {
  advertiserId: string | number;

  constructor(
    advertiserId: string | number,
    adRequestSourceManager: AdRequestSourceManager = new DefaultAdRequestSourceManager(),
    manager: AdvertiserManager = new DefaultAdvertiserManager(),
    agencyManager: AgencyManager = new DefaultAgencyManager()
  ) {
    super(adRequestSourceManager, manager, agencyManager);
    this.advertiserId = advertiserId;
  }

  get title() {
    return i18n.t('advertiserForm.labels.editTitle');
  }

  get breadcrumbs() {
    const advertiserName = _.get(this.advertiser, 'advertiserName');
    return [
      {
        path: '/advertisers',
        breadcrumb: i18n.t('appMenus.comapnyManagement.items.advertisers')
      },
      {
        path: '/advertisers/:advertiserId',
        breadcrumb: DynamicBreadcrumb,
        props: { label: advertiserName, matchParam: 'advertiserId' }
      },
      {
        path: '/advertisers/:advertiserId/edit',
        breadcrumb: DynamicBreadcrumb,
        props: {
          prefix: i18n.t('common.labels.edit'),
          label: advertiserName,
          matchParam: 'advertiserId'
        }
      }
    ];
  }

  async initAdvertiser(): Promise<void> {
    const advertiser = await this.manager.getAdvertiser(this.advertiserId);
    await this.initPublisherOptions(advertiser.agencyId);
    this.advertiser = advertiser;
  }

  async submit() {
    if (!this.formikProps) {
      return;
    }

    const editedAdvertiser = { ...this.formikProps.values };
    this.updateState(true);
    try {
      await this.manager.updateAdvertiser(editedAdvertiser);
      this.updateState(false, this.getRedirectPath(this.advertiserId));
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.updateState(false);
    }
  }
}
